import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";
import {
  FaUser,
  FaIdCard,
  FaFemale,
  FaBirthdayCake,
  FaCheckCircle,
  FaBuilding,
  FaBarcode,
  FaBriefcase,
  FaTag,
  FaRegCalendarAlt,
} from "react-icons/fa";
import TitleStripe from "../../common/TitleStripe";

const formatCPF = (cpf) => {
  if (!cpf) return null;
  const digits = String(cpf).replace(/\D/g, "");
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const formatCNPJ = (cnpj) => {
  if (!cnpj) return null;
  const digits = String(cnpj).replace(/\D/g, "");
  if (digits.length !== 14) return cnpj;
  return digits.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
};

const CadastralItem = ({ icon: Icon, label, value, full }) => (
  <div
    className={`flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3 transition hover:border-blue-200 hover:bg-blue-50/40 ${
      full ? "sm:col-span-2" : ""
    }`}
  >
    {Icon && (
      <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-blue-100 text-blue-600">
        <Icon className="h-4 w-4" />
      </span>
    )}
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="break-words text-sm font-medium text-gray-800">
        {value || "Indisponível"}
      </p>
    </div>
  </div>
);

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "sim", "s"].includes(normalized)) return true;
    if (["false", "0", "nao", "não", "n"].includes(normalized)) return false;
  }

  return Boolean(value);
};

const PotencialPagamentoQuery = ({ data }) => {
  const limpaNome = toBoolean(data?.limpaNome);
  const isMarketAnalysis = limpaNome === true;

  const Icon = isMarketAnalysis ? HiExclamationCircle : HiCheckCircle;
  const title = isMarketAnalysis
    ? "Analisar Mais Dados de Mercado"
    : "Dentro do padrão de mercado";
  const description = isMarketAnalysis
    ? "O perfil merece uma leitura complementar com indicadores de mercado antes da decisão."
    : "O perfil está alinhado aos sinais esperados para o padrão de mercado neste indicador.";

  const cadastro = data?.dadosCadastrais;
  const isEmpresa = Boolean(cadastro?.cnpj || cadastro?.razaoSocial);
  const situacaoUpper = cadastro?.situacao?.toUpperCase() || "";
  const situacaoOk =
    situacaoUpper.includes("REGULAR") || situacaoUpper.includes("ATIVO");
  const headerTitle = isEmpresa ? cadastro?.razaoSocial : cadastro?.nome;

  return (
    <section className="my-6 space-y-4">
      {cadastro && (
        <div>
          <TitleStripe className="mb-4">Dados Cadastrais</TitleStripe>
          <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Cabeçalho */}
            <div className="flex flex-col gap-3 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-white/20 backdrop-blur">
                  {isEmpresa ? (
                    <FaBuilding className="h-6 w-6" />
                  ) : (
                    <FaUser className="h-6 w-6" />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wider text-blue-100">
                    {isEmpresa ? "Dados da Empresa" : "Dados Cadastrais"}
                  </p>
                  <h3 className="truncate text-lg font-bold leading-tight">
                    {headerTitle || "Indisponível"}
                  </h3>
                  {isEmpresa && cadastro?.nomeFantasia && (
                    <p className="truncate text-sm text-blue-100">
                      {cadastro.nomeFantasia}
                    </p>
                  )}
                </div>
              </div>
              {cadastro?.situacao && (
                <span
                  className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                    situacaoOk
                      ? "bg-green-400/20 text-green-50"
                      : "bg-red-400/20 text-red-50"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      situacaoOk ? "bg-green-300" : "bg-red-300"
                    }`}
                  />
                  {cadastro.situacao}
                </span>
              )}
            </div>

            {/* Conteúdo */}
            <div className="grid grid-cols-1 gap-3 p-6 sm:grid-cols-2">
              {isEmpresa ? (
                <>
                  <CadastralItem
                    icon={FaBarcode}
                    label="CNPJ"
                    value={formatCNPJ(cadastro?.cnpj)}
                  />
                  <CadastralItem
                    icon={FaRegCalendarAlt}
                    label="Data de Fundação"
                    value={cadastro?.dataFundacao}
                  />
                  <CadastralItem
                    icon={FaBuilding}
                    label="Razão Social"
                    value={cadastro?.razaoSocial}
                  />
                  <CadastralItem
                    icon={FaTag}
                    label="Nome Fantasia"
                    value={cadastro?.nomeFantasia}
                  />
                  <CadastralItem
                    icon={FaBriefcase}
                    label="Ramo de Atividade"
                    value={cadastro?.ramoAtividade}
                    full
                  />
                  <CadastralItem
                    icon={FaCheckCircle}
                    label="Situação"
                    value={cadastro?.situacao}
                    full
                  />
                </>
              ) : (
                <>
                  <CadastralItem
                    icon={FaIdCard}
                    label="CPF"
                    value={formatCPF(cadastro?.cpf)}
                  />
                  <CadastralItem
                    icon={FaBirthdayCake}
                    label="Data de Nascimento"
                    value={cadastro?.dataNascimento}
                  />
                  <CadastralItem
                    icon={FaUser}
                    label="Nome"
                    value={cadastro?.nome}
                  />
                  <CadastralItem
                    icon={FaFemale}
                    label="Nome da Mãe"
                    value={cadastro?.nomeMae}
                  />
                  <CadastralItem
                    icon={FaCheckCircle}
                    label="Situação"
                    value={cadastro?.situacao}
                    full
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        <TitleStripe className="mb-4">Potencial de Pagamento</TitleStripe>

        <div
          className={`overflow-hidden rounded-lg border ${
            isMarketAnalysis
              ? "border-red-200 bg-red-50"
              : "border-green-200 bg-green-50"
          }`}
        >
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                isMarketAnalysis
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              <Icon className="h-7 w-7" />
            </div>

            <div className="min-w-0 flex-1">
              <div
                className={`text-lg font-bold ${
                  isMarketAnalysis ? "text-red-900" : "text-green-900"
                }`}
              >
                {title}
              </div>
              <p
                className={`mt-1 text-sm ${
                  isMarketAnalysis ? "text-red-800" : "text-green-800"
                }`}
              >
                {description}
              </p>
            </div>

            <div
              className={`w-fit rounded-md px-3 py-2 text-xs font-semibold uppercase ${
                isMarketAnalysis
                  ? "bg-white text-red-700 ring-1 ring-red-200"
                  : "bg-white text-green-700 ring-1 ring-green-200"
              }`}
            >
              {isMarketAnalysis ? "Atenção" : "OK"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PotencialPagamentoQuery;
